import React from 'react';
import { Lock, Zap } from 'lucide-react';
import bseBullImage from '../../assets/bse-bull.png';
import { useLiveIpoFeed } from '../../hooks/useLiveIpoFeed';

export const HeroTerminal = () => {
  // Live IPO quotes: GET /api/market/gmp + /api/market/subscription
  const { items } = useLiveIpoFeed();
  const quotes = items.filter((item) => item.price || item.gmp);

  return (
    <div className="relative w-full max-w-2xl lg:max-w-xl xl:max-w-2xl 2xl:max-w-[740px] mx-auto lg:mx-0">
      
      {/* Floating Zero Latency Feed Badge - Matching Second Image */}
      <div className="absolute -top-4 right-2 sm:right-6 z-20 flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-4 py-2 rounded-2xl border border-slate-200/90 shadow-elevated transition-transform hover:scale-105">
        <div className="w-6 h-6 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
          <Zap className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
        </div>
        <div className="text-left">
          <div className="text-xs font-bold text-slate-900 leading-tight">Live IPO Feed</div>
          <div className="text-[10px] text-slate-500 font-medium">Grey market &amp; NSE bid book</div>
        </div>
      </div>

      {/* Monitor Display Frame */}
      <div className="relative rounded-2xl sm:rounded-3xl p-2 sm:p-2.5 bg-gradient-to-b from-slate-900 via-slate-950 to-black shadow-2xl ring-1 ring-slate-800">
        
        {/* Inner Screen Display */}
        <div className="relative rounded-xl sm:rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 flex flex-col">
          
          {/* Top macOS Browser Window Header */}
          <div className="bg-slate-900/90 backdrop-blur px-3 sm:px-4 py-2 border-b border-slate-800/80 flex items-center justify-between z-10">
            {/* Window control dots */}
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm" />
            </div>

            {/* Address Bar */}
            <div className="flex items-center gap-1.5 bg-slate-950/90 text-slate-400 px-3.5 py-1 rounded-md border border-slate-800 text-[11px] font-mono max-w-[210px] sm:max-w-xs truncate shadow-inner">
              <Lock className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              <span className="truncate">alambrix.com/terminal/live-ipo</span>
            </div>

            {/* Subtle empty spacer for symmetry */}
            <div className="w-10" />
          </div>

          {/* Screen Content: BSE Phiroze Jeejeebhoy Towers & Bull Artwork */}
          <div className="relative w-full aspect-[16/10] sm:aspect-[16/9.5] bg-white overflow-hidden flex items-center justify-center">
            <img
              src={bseBullImage}
              alt="BSE Phiroze Jeejeebhoy Towers and Charging Bull"
              className="w-full h-full object-contain sm:object-cover object-center"
            />

            {/* Live IPO quotes running across the screen */}
            {quotes.length > 0 && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/90 via-slate-900/80 to-transparent pt-4 pb-2 px-2 z-10">
              <div className="flex items-center overflow-hidden border-t border-emerald-500/30 bg-slate-950/85 backdrop-blur-md rounded-lg py-1 px-2 shadow-lg">
                <div className="flex-shrink-0 flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold tracking-wider mr-2 uppercase">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400"></span>
                  </span>
                  <span>IPO LIVE</span>
                </div>

                <div className="flex-1 overflow-hidden relative">
                  <div className="flex items-center space-x-6 whitespace-nowrap animate-marquee">
                    {[...quotes, ...quotes].map((quote, idx) => (
                      <div key={`${quote.id}-${idx}`} className="inline-flex items-center gap-1.5 text-[11px]">
                        <span className="font-semibold text-slate-200">{quote.name}</span>
                        {quote.price && <span className="text-slate-400 font-mono">{quote.price}</span>}
                        {quote.gmp && (
                          <span className={`font-bold font-mono flex items-center ${quote.gmpPercent < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                            {quote.gmpPercent < 0 ? '▼' : '▲'} GMP {quote.gmp}
                          </span>
                        )}
                        <span className="text-slate-600 ml-2 font-light">•</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

        </div>

        {/* Screen Bezel Bottom Chin */}
        <div className="h-2 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-b-xl opacity-70 mt-0.5 mx-10" />
      </div>

      {/* Realistic iMac-style Desktop Stand */}
      <div className="flex flex-col items-center -mt-1">
        {/* Stand Neck */}
        <div className="w-16 h-8 sm:h-10 bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300 shadow-inner rounded-t-sm" />
        {/* Stand Foot / Base */}
        <div className="w-40 sm:w-48 h-2 sm:h-2.5 bg-gradient-to-b from-slate-200 to-slate-300 rounded-full shadow-md -mt-0.5" />
        {/* Soft Drop Shadow under Stand */}
        <div className="w-48 sm:w-60 h-2.5 bg-slate-400/25 blur-md rounded-full -mt-1" />
      </div>

    </div>
  );
};

export default HeroTerminal;
